import React from "react";
import { Box, Container, VStack, Grid, Heading, Text, HStack } from "@chakra-ui/react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/common/Hero";
import DisciplineCard from "../components/common/DisciplineCard";
import CTAButton from "../components/common/CTAButton";
import { disciplines } from "../data/disciplines";

export default function Disciplines() {
  return (
    <Box>
      <Navbar />

      {/* Hero Section */}
      <Hero
        title="Master Industry-Aligned Disciplines"
        subtitle="Choose the path that matches your career goals. Each discipline is designed for specific market demands and built with real companies."
        badge="6 Disciplines, 1000+ Job Roles"
        withGradient
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <CTAButton to="/for-students" size="lg">
            Explore as a Student
          </CTAButton>
          <CTAButton to="/contact" variant="outline" size="lg">
            Get Recommendations
          </CTAButton>
        </HStack>
      </Hero>

      {/* About Disciplines Section */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color="#1E293B">
                Why These 6 Disciplines?
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Each discipline addresses specific market demands, skill gaps, and growth opportunities in India's tech industry.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#10B981" mb={3}>
                  100% Job-Ready
                </Heading>
                <Text color="#475569">
                  Learn exactly what companies are hiring for. Our curriculum is built with actual job descriptions and industry feedback.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#10B981" mb={3}>
                  Real Projects & Portfolio
                </Heading>
                <Text color="#475569">
                  Build production-grade projects. By the end, you have a verified portfolio that actually gets you interviews.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#10B981" mb={3}>
                  Growing Markets
                </Heading>
                <Text color="#475569">
                  Each discipline targets growing sectors with 3-5 year job outlook. Not skills that go obsolete next year.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#10B981" mb={3}>
                  Multiple Career Paths
                </Heading>
                <Text color="#475569">
                  Each discipline leads to 150+ job roles. You're not locked into one path - you have options.
                </Text>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Disciplines Grid */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Our 6 Disciplines
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Click on any discipline to see the full curriculum, skills, projects, and career paths.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {disciplines.map((disc) => (
                <DisciplineCard
                  key={disc.id}
                  id={disc.id}
                  name={disc.name}
                  description={disc.description}
                  icon={disc.icon}
                />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Selection Guide Section */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color="#1E293B">
                Unsure Which Discipline to Choose?
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Take our quick assessment to find your perfect fit based on your interests and career goals.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} w="full">
              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Love Building Products?
                </Heading>
                <Text color="#475569" mb={4}>
                  Full-Stack & AI Engineering or Mobile Development might be your calling.
                </Text>
                <CTAButton to="/disciplines/full-stack-ai" size="sm">
                  Explore Full-Stack
                </CTAButton>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Interested in Data?
                </Heading>
                <Text color="#475569" mb={4}>
                  Data Science or Data Analytics opens doors to high-paying roles.
                </Text>
                <CTAButton to="/disciplines/data-science" size="sm">
                  Explore Data Science
                </CTAButton>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Security & Operations?
                </Heading>
                <Text color="#475569" mb={4}>
                  Cybersecurity or DevOps/SRE offer premium salaries and stability.
                </Text>
                <CTAButton to="/disciplines/cybersecurity" size="sm">
                  Explore Cybersecurity
                </CTAButton>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        bgGradient="linear(to-br, #0F172A, #1E293B, #10B981)"
        py={{ base: 12, md: 20 }}
        color="white"
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="2xl" color="white">
              Ready to Master Your Discipline?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="#FFFFFF">
              Get industry-ready skills, build your portfolio, and land your dream job straight from college.
            </Text>

            <HStack spacing={4} justify="center" flexWrap="wrap">
              <CTAButton to="/for-students" size="lg">
                Start Learning
              </CTAButton>
              <CTAButton to="/contact" variant="outline" size="lg">
                Talk to an Advisor
              </CTAButton>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
